<script lang="ts" setup>
import { copyToClipboard } from '@/helpers/copyToClipboard';
import { computed } from 'vue';
import { PendingInvite } from '@/types';
import { formatDate } from './formatDate';
import dayjs from 'dayjs';
import { solanaFactor } from '@/constants';
import useInvitation from './useInvitation';
import { useMutation } from '@tanstack/vue-query';
import { captureException } from '@sentry/vue';
import { snackbarPush } from '@/helpers/snackbarPush';
import usePendings from './usePendings';
import USDValue from '@/components/molecules/USDValue.vue';
import { useIsMobile } from '@/helpers/useIsMobile';
import useExchangeRate from './useExchangeRate';

const props = defineProps<{ data: PendingInvite }>();

const { data: exchangeRate } = useExchangeRate();

const emit = defineEmits(['close']);

const url = computed(() => `https://jup.ag/invite/${props.data.code}`);

const { isMobile } = useIsMobile();

const canShare = computed(() => navigator.share != null && isMobile.value);

const amount = computed(() => Number(props.data.amount) / solanaFactor);

const text = computed(() => {
  const USDValue = (amount.value * (exchangeRate?.value ?? 0)).toFixed(2);
  return `I just sent you $${USDValue}! 🎉

Tap this link to claim it:
👉 ${url.value}

If you don’t have the app yet, the link will guide you to download Jupiter Mobile and claim your SOL. 

Alternatively, you can manually enter the code ${props.data.code} in the app to claim.`;
});

const { clawback } = useInvitation();

const { refetch } = usePendings();

const { mutate: cancelInvite, isPending } = useMutation({
  mutationFn: clawback,
  onSuccess: () => {
    refetch();
    snackbarPush('Invite canceled.');
    emit('close');
  },
  onError: (error) => {
    console.error(error);
    captureException(error);
  },
});

const share = () => {
  navigator.share({
    url: url.value,
    text: text.value,
  });
};
</script>

<template>
  <v-card class="p-8 flex flex-column gap-4 items-center bg-[#051723]">
    <p class="text-3xl font-bold text-center">Invite created!</p>

    <img src="/success.png" alt="Success" class="w-1/2" />

    <div class="flex flex-col items-center">
      <p class="font-bold text-center">You gifted</p>
      <p class="text-3xl font-bold">{{ amount }} SOL</p>
      <USDValue :value="amount" class="opacity-50" />
    </div>

    <!-- <div class="flex flex-column gap-1 items-center">
      <p>Send this link to your friend:</p>
      <button
        class="flex flex-row gap-2 items-center p-2 bg-white/10 rounded-jup text-center"
        @click="copyToClipboard(url, 'Invite link copied!')"
      >
        {{ url }}
        <v-icon icon="mdi-clipboard-text-outline" size="small" />
      </button>
    </div>

    <p class="text-center text-sm">
      He can alternatively, enter the code
      <span class="font-bold">{{ data.code }}</span> in the app to claim.
    </p> -->

    <ul
      class="flex flex-column gap-1 w-full bg-[#051D2A] rounded-jup divide-y divide-brand-background"
    >
      <li
        class="flex flex-row justify-between p-3 items-center"
        v-if="data.code != null"
      >
        <span>Invite code</span>
        <button
          class="font-bold"
          @click="
            copyToClipboard(data.code ?? 'MISSING', 'Invite code copied!')
          "
        >
          {{ data.code }}
          <v-icon icon="mdi-content-copy" size="small" class="ml-1" />
        </button>
      </li>
      <li class="flex flex-row justify-between p-3">
        <span>Date created</span>
        <span class="font-bold">
          {{ formatDate(data.creation_time) }}
        </span>
      </li>
      <li class="flex flex-row justify-between p-3">
        <span>Expiry</span>
        <span class="font-bold">{{
          dayjs(Number(data.expiry) * 1000).fromNow()
        }}</span>
      </li>
    </ul>

    <div v-if="data.code != null" class="w-full">
      <v-btn
        v-if="canShare"
        @click="share"
        class="w-full"
        size="large"
        variant="elevated"
        color="primary"
      >
        <v-icon icon="mdi-export-variant" size="small" class="mr-2" />
        Share
      </v-btn>
      <v-btn
        v-else
        @click="copyToClipboard(text, 'Invite link copied!')"
        class="w-full"
        size="large"
        variant="elevated"
        color="primary"
      >
        <v-icon icon="mdi-content-copy" size="small" class="mr-2" />
        Copy link
      </v-btn>
    </div>
    <v-btn
      v-if="data.mode != 'creation'"
      class="w-full"
      size="large"
      :disabled="isPending"
      :loading="isPending"
      variant="tonal"
      color="red"
      @click="cancelInvite(data)"
    >
      Cancel invite
    </v-btn>
  </v-card>
</template>
